import { rpx, useResponsivePx, useTranslatable } from '../helpers';
import Typography from './typography';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFastLane } from '../store';
import { CSS, FastLaneSection, type Translatable } from '../types';
import SectionFooter from './section-footer';
import { usePixelMap } from '../store/fast-lane-context';

interface SectionContentBodyProps {
    section: FastLaneSection;
    index: number;
}

export default function SectionContentBody({ section, index }: SectionContentBodyProps) {
    const codificationTrigger = useFastLane(f => f.codificationTrigger);
    const theme = useFastLane(f => f.getTheme());
    const translatableHeading = useFastLane(f => f.getActiveHeading(index));
    const isDark = useFastLane(f => f.isBackgroundDark());
    const activeHeading = useTranslatable(translatableHeading);
    const eyebrow = useTranslatable(section.eyebrow);
    const playingIndex = useFastLane(f => f.playingIndex);
    const sceneCount = useFastLane(f => f.getSceneCount());
    const footer = useMemo(() => {
        const index = playingIndex % sceneCount;

        // check if array
        if (Array.isArray(section.footer.content)) {
            return section.footer?.content[index === 2 ? 1 : index] ?? section.footer.content[0];
        }

        return section.footer.content;
    }, [playingIndex]);
    const footerContent = useTranslatable(footer);
    const pixelMap = usePixelMap();
    const responsivePx = useResponsivePx();

    const responsive = useMemo(() => {
        return {
            showFooter: pixelMap.contentHeight > 120,
        };
    }, [pixelMap]);

    const marginLeft = theme?.leftBorder?.marginRight ?? 0;
    const eyebrowStyle = useMemo(() => {
        const { fontSize, lineHeight, ...other } = theme?.style?.eyebrow ?? {};
        if (pixelMap.contentHeight > 120) {
            return {
                fontSize,
                lineHeight,
                minHeight: fontSize,
                marginLeft: rpx(3, 'positioning'),
                ...other,
            };
        }

        return {
            fontSize: responsivePx(fontSize, 2),
            lineHeight: responsivePx(lineHeight, 2),
            minHeight: responsivePx(fontSize, 2),
            ...other,
        };
    }, [pixelMap, theme]);

    const footerStyle = useMemo(() => {
        const { fontSize, lineHeight, ...other } = theme?.style?.footer ?? {};
        if (pixelMap.contentHeight > 120) {
            return {
                fontSize,
                lineHeight,
                minHeight: fontSize,
                marginLeft: rpx(3, 'positioning'),
                ...other,
            };
        }

        return {
            fontSize: responsivePx(fontSize, 2),
            lineHeight: responsivePx(lineHeight, 2),
            minHeight: responsivePx(fontSize, 2),
            ...other,
        };
    }, [pixelMap, theme]);

    const headlineStyle = useMemo(() => {
        const { fontSize, lineHeight, ...other } = theme.style.headline;
        if (pixelMap.contentHeight > 120) {
            return {
                fontSize,
                lineHeight,
                minHeight: fontSize,
                ...other,
            };
        }

        return {
            ...other,
            marginTop: responsivePx(5),
            fontSize: responsivePx(fontSize, 1.7),
            lineHeight: responsivePx(lineHeight, 1.7),
            minHeight: responsivePx(fontSize, 1.7),
        };
    }, [pixelMap, theme]);

    return (
        <div
            style={{
                //WebkitFontSmoothing: "antialiased",
                position: 'absolute',
                top: responsivePx(13),
                left: Number(rpx(8, 'positioning', { raw: true })) + marginLeft,
                height: `calc(100% - ${responsivePx(13)}px)`,
                width: '100%',
                zIndex: 5,
            }}
        >
            {theme.includeLogo && (
                <div
                    style={{
                        height: rpx(50, 'positioning'),
                        // display: 'flex',
                        // alignItems: 'center',
                        paddingTop: rpx(10, 'positioning'),
                        paddingBottom: rpx(8, 'positioning'),
                    }}
                >
                    {section?.logo?.url ? (
                        <img
                            src={section?.logo?.url}
                            style={{
                                height: '100%',
                                width: 'auto',
                                paddingTop: rpx(1, 'positioning'),
                                paddingBottom: rpx(1, 'positioning'),
                                // width: rpx(200, 'positioning'),
                                filter: isDark ? 'invert(1)' : undefined,
                                // marginLeft: rpx(-8, 'positioning'),
                            }}
                        />
                    ) : (
                        <Eyebrow
                            eyebrowStyle={{
                                ...eyebrowStyle,
                                marginBottom: rpx(2.5, 'positioning'),
                                marginLeft: rpx(-2.5, 'positioning'),
                            }}
                            eyebrow={eyebrow}
                            theme={theme}
                        />
                    )}
                </div>
            )}

            {
                theme.useHeadingMedia ? (
                    <div
                        style={{
                            // height: 120,
                            marginTop: -3,
                        }}
                    >
                        <img
                            src={section?.headingLogo?.url}
                            style={{
                                height: '35px',
                                width: 'auto',
                                paddingTop: rpx(3, 'positioning'),
                                paddingBottom: rpx(3, 'positioning'),
                                // width: rpx(200, 'positioning'),
                                filter: 'invert(1)',
                                // marginLeft: rpx(-8, 'positioning'),
                            }}
                        />
                        {
                            !theme?.includeLogo && (
                                <Eyebrow
                                    eyebrowStyle={{
                                        ...eyebrowStyle,
                                        marginBottom: rpx(2.5, 'positioning'),
                                    }}
                                    eyebrow={eyebrow}
                                    theme={theme}
                                />
                            )
                        }
                    </div>
                ) : (
                    <>
                        {
                            !theme?.includeLogo && (
                                <Eyebrow
                                    eyebrowStyle={{
                                        ...eyebrowStyle,
                                        marginBottom: rpx(2.5, 'positioning'),
                                    }}
                                    eyebrow={eyebrow}
                                    theme={theme}
                                />
                            )
                        }
                        <Typography
                            style={{
                                ...headlineStyle,
                                color: theme.textColor,
                            }}
                            text={activeHeading}
                            updater={codificationTrigger}
                            codification={theme.codification === 'all' || theme.codification.includes('heading')}
                        />
                    </>
                )
            }

            {
                theme?.includeLogo && (
                    <Eyebrow
                        eyebrowStyle={{
                            ...footerStyle,
                            position: 'absolute',
                            bottom: rpx(18),
                        }}
                        eyebrow={footerContent}
                        theme={theme}
                    />
                )
            }

            {
                responsive.showFooter && (
                    <SectionFooter
                        section={section}
                        index={index}
                    />
                )
            }
        </div>
    );
}

function Eyebrow({ eyebrowStyle, eyebrow, theme }: { eyebrowStyle: CSS, eyebrow: string, theme: any }) {
    return (
        <Typography
            style={{
                ...eyebrowStyle,
                color: theme.textColor,
            }}
            text={eyebrow}
            codification={theme.codification === 'all' || theme.codification.includes('eyebrow')}
        />
    );
}
